import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import {
  AuthRepository,
  getStoredToken,
  getUser,
} from 'app/state/auth.repository';
import { AuthService } from 'app/state/auth.service';
import { LocalStorageService } from './local-storage-service';
import { TranslocoService } from '@ngneat/transloco';
import { environment } from 'environments/environment';
import { NotificationsService } from 'app/state/notifications.service';
import { NotificationsRepository } from 'app/state/notifications.repository';
import { CoachMenteeChatService } from './coachMenteeChat.service';

@Injectable({ providedIn: 'root' })
export class TenantSyncService {
  private connection: signalR.HubConnection;

  public connect(): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    return this.connection.start().catch((err) => {
      console.error('SignalR connection failed:', err);
      throw err;
    });
  }

  constructor(
    private auth: AuthService,
    public generalDataRepo: GeneralDataRepository,
    public router: Router,
    private storage: LocalStorageService,
    private authRepo: AuthRepository,
    private notificationsService: NotificationsService,
    private notificationsRepo: NotificationsRepository,
    private translocoService: TranslocoService,
    private chatService: CoachMenteeChatService
  ) {
    const user = getStoredToken();

    let url = '/tenantHub';
    if (environment.production) {
      url = '/core/tenantHub';
    }
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => user ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on('TenantChanged', (newTenantId: string) => {
      console.log(newTenantId);
      const currentTenantId = localStorage.getItem('activeTenantId');
      if (currentTenantId === newTenantId) {
        return;
      }

      if (newTenantId) {
        localStorage.setItem('activeTenantId', newTenantId);
      } else {
        localStorage.removeItem('activeTenantId');
      }
      let token = getStoredToken();
      this.auth.setActiveTenant(token!, newTenantId).subscribe(() => {
        this.generalDataRepo.updateAllGeneralData(true);
        this.router.navigate(['/home']).then(() => {
          storage.setTenantId(newTenantId);
        });
      });
    });

    this.connection.on(
      'ImpersonateStarted',
      (newUserId: string, newTenantId: string) => {
        const user = getUser();
        if (user?.id === newUserId) {
          return;
        }

        this.auth.impersonate(newUserId!, newTenantId).subscribe({
          next: (responce) => {
            if (responce.user) {
              localStorage.setItem('isImpersonatingMode', 'true');
              this.authRepo.setUser(responce.user);
              this.translocoService.setActiveLang(
                responce?.user?.language ?? 'en'
              );
              if (newTenantId) {
                localStorage.setItem('activeTenantId', newTenantId);
              } else {
                localStorage.removeItem('activeTenantId');
              }
            }
            this.authRepo.setToken(responce.token);
            window.location.reload();
          },
        });
      }
    );

    this.connection.on('ImpersonateStoped', (resp: any) => {
      this.auth.unimpersonateWithoutRequest(resp);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    });

    this.connection.on('ReloadNotifications', (resp: any) => {
      this.notificationsService.loadAllCount().subscribe((x) => {
        this.notificationsRepo.notificationCount$.next(x);
      });
    });

    this.connection.on('ReloadMessages', (resp: any) => {
      this.chatService.getAllMessagesForUser().subscribe((chats) => {
        const allMessages = chats.flatMap((c) => c.messages);
        this.generalDataRepo.updateMessages(allMessages);
      });
    });
    this.connection.on('ReloadGroupMessages', (resp: any) => {
      this.chatService.getAllMessagesForUserGroup().subscribe((chats) => {
        const allMessages = chats.flatMap((c) => c.messages);
        this.generalDataRepo.updateGroupMessages(allMessages);
      });
    });
    this.connection.on('LogOut', (resp: any) => {
      localStorage.clear();
      localStorage.setItem('isImpersonatingMode', 'false');
      this.router.navigate(['/signin']);
      // this.generalDataRepo.updateAllGeneralData(true);
    });

    this.connection
      .start()
      .catch((err) => console.error('SignalR error:', err));
  }
}
